import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';

interface PlanBadgeProps {
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  cancelAt: string | null;
  pulse?: boolean;
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function PlanBadge({
  currentPeriodEnd,
  cancelAtPeriodEnd,
  cancelAt,
  pulse = false,
}: PlanBadgeProps) {
  const renewFormatted = formatDate(currentPeriodEnd);
  const cancelAtFormatted = formatDate(cancelAt);

  let label: string;
  if (cancelAt) {
    label = cancelAtFormatted ? `Ends ${cancelAtFormatted}` : 'Canceled';
  } else if (cancelAtPeriodEnd) {
    label = renewFormatted ? `Ends ${renewFormatted}` : 'Canceled';
  } else {
    label = renewFormatted ? `Renews ${renewFormatted}` : 'Unlimited access';
  }

  const pulseAnimation = pulse
    ? {
        boxShadow: [
          '0 0 0 0 rgba(212, 163, 115, 0)',
          '0 0 0 10px rgba(212, 163, 115, 0.35)',
          '0 0 0 18px rgba(212, 163, 115, 0)',
        ],
      }
    : undefined;

  return (
    <motion.div
      animate={pulseAnimation}
      transition={
        pulse
          ? { duration: 1.6, ease: 'easeOut', delay: 0.3 }
          : undefined
      }
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-primary-dark p-5 text-white"
    >
      <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full bg-white/[0.06] pointer-events-none" />
      <div className="relative flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
          <Sparkles size={20} className="text-white" />
        </div>
        <div className="min-w-0">
          <p className="font-sans text-xs font-semibold uppercase tracking-wide text-white/70">
            Premium
          </p>
          <p className="font-sans text-sm text-white/95 mt-0.5">{label}</p>
        </div>
      </div>
    </motion.div>
  );
}
